import React, { createContext, useContext, useEffect, useState } from 'react';
import { Socket, io } from 'socket.io-client';
import { api } from '../services/api';
import { ChatMessageType, ChatType, SendMessageType } from '../services/apiTypes';
import { createChatRoom } from '../utils/apiFunctions';
import { useAuth } from './auth';

interface MessagesContextData {
  chatModal: boolean;
  setChatModal: (value: boolean) => void;
  socket: Socket | null;
  chatSolicitations: number;
  selectedChat: ChatType | undefined;
  setSelectedChat: (chat: ChatType | undefined) => void;
  chats: ChatType[];
  createChat: (chatName: string, receivers: string[]) => void;
  sendMessage: (message: SendMessageType) => void;
}

const MessagesContext = createContext<MessagesContextData>({
  chatModal: false,
  setChatModal: () => { },
  socket: null,
  chatSolicitations: 0,
  selectedChat: undefined,
  setSelectedChat: () => { },
  chats: [],
  createChat: async () => { },
  sendMessage: async () => { },
});

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const [chatModal, setChatModal] = useState<boolean>(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [chatSolicitations, setChatSolicitations] = useState<number>(0);
  const [selectedChat, setSelectedChat] = useState<ChatType | undefined>(undefined);
  const [chats, setChats] = useState<ChatType[]>([]);

  useEffect(() => {
    if (token && token.accessToken !== '') {
      getChats();
      connectSocket();
    }
  }, [token]);

  const connectSocket = async () => {
    if (!token || socket) return;
    const url = process.env.NODE_ENV === "development" ? 'localhost:3030' : 'http://geetz-backend-staging-29ff4637f59b4e60.elb.us-east-1.amazonaws.com/';
    const newSocket = io(url, {
      autoConnect: true,
      extraHeaders: {
        Authorization: `Bearer ${token.accessToken}`,
      },
      auth: {
        token: token.accessToken,
      },
    });
    setSocket(newSocket);
  }

  const [messageReceived, setMessageReceived] = useState<ChatMessageType | null>(null);

  useEffect(() => {
    if (socket) {
      socket.on('connect', () => {
        console.log('Connected to server');
      });

      socket.on('disconnect', () => {
        console.log('Disconnected from server');
      });

      socket.on('error', (error: any) => {
        setSocket(null);
      });

      socket.on('receiveMessage', (message: any) => {
        // console.log('receiveMessage', JSON.stringify(message, null, 2))
        // onMessageReceived(message);
        setMessageReceived(message);
      });
    }
  }, [socket]);

  useEffect(() => {
    if (chatModal) {
      setChatSolicitations(0);
    }
  }, [chatModal]);

  useEffect(() => {
    if (messageReceived) {
      solicitationsCounter();
      onMessageReceived(messageReceived);
      setMessageReceived(null);
    }
  }, [messageReceived]);

  const solicitationsCounter = async () => {
    if (!chatModal) {
      setChatSolicitations(chatSolicitations + 1);
    }
  }

  const onMessageReceived = async (message: ChatMessageType) => {
    // console.log('onMessageReceived', message);
    let newChats: ChatType[] = [];
    chats.map((chat: ChatType) => {
      console.log('chat.id', chat);
      if (chat.id === message.room.id) {
        newChats.push({
          ...chat,
          messages: [...chat.messages ? chat.messages : [], message],
        });
      } else {
        newChats.push(chat);
      }
    });
    if (selectedChat && (selectedChat.id === message.room.id || selectedChat.id === message.parentId)) {
      setSelectedChat({
        ...selectedChat,
        messages: [...selectedChat.messages ? selectedChat.messages : [], message],
      });
    }
    if (newChats.length === 0) return;
    setChats(newChats);
  };

  const createChat = async (chatName: string, receivers: string[]) => {
    let hasChat = false;
    chats.map((chat) => {
      if (chat.id === chat.id) {
        hasChat = true;
      }
    });
    if (hasChat) return;
    const newChat = await createChatRoom({
      name: chatName,
      receiverUserIds: receivers,
      type: 'private',
    });
    if (newChat) {
      setChats([...chats, newChat]);
      setSelectedChat(newChat);
      setChatModal(true);
    }
  };

  const sendMessage = async (message: SendMessageType) => {
    api.post(`chat/messages/send`, message).then((response) => {
      // console.log('sendMessage', JSON.stringify(response.data, null, 2));
      // onMessageReceived(response.data);
    });
  }

  const getChats = async () => {
    try {
      const response = await api.get('chat/rooms');
      if (response.data.length === 0) return;
      setChats(response.data);
    } catch (error) {
      console.error(error);
    }
  };


  return (
    <MessagesContext.Provider value={{
      chatModal,
      setChatModal,
      socket,
      chatSolicitations,
      selectedChat,
      setSelectedChat,
      chats,
      createChat,
      sendMessage
    }}>
      {children}
    </MessagesContext.Provider>
  );
};

export const useMessages = () => {
  return useContext(MessagesContext);
};